import { getHeaderHtml, parseHeader } from "../../../../util/headerUtil";
import { calculateAgeDetailed } from "../../../../util/ageCalculator";

// Translation Maps
const translations = {
  marathi: {
    "After Breakfast": "नंतर",
    "Before Breakfast": "आधी",
    "After Lunch": "नंतर",
    "Before Lunch": "आधी",
    "After Dinner": "नंतर",
    "Before Dinner": "आधी",
    "After": "नंतर",
    "Before": "आधी",
    "Daily": "दररोज",
    "Days": "दिवस",
    "Month": "महिना",
    "Weeks": "आठवडे",
    "Night:": "रात्री:",
    "Noon:": "दुपारी:",
    "Morn:": "सकाळी:",
    "B:": "स. -",
    "L:": "दु. -",
    "D:": "रा. -",
    "Take after food": "जेवणानंतर घ्या",
    "Chewable": "चघळून खा"
  },
  hindi: {
    "After Breakfast": "बाद",
    "Before Breakfast": "पहले",
    "After Lunch": "बाद",
    "Before Lunch": "पहले",
    "After Dinner": "बाद",
    "Before Dinner": "पहले",
    "After": "बाद",
    "Before": "पहले",
    "Daily": "प्रतिदिन",
    "Days": "दिन",
    "Month": "महीना",
    "Weeks": "हफ्ते",
    "Morning": "सुबह",
    "Noon": "दोपहर",
    "Night": "रात",
    "Morn": "सुबह",
    "Night:": "रात:",
    "Noon:": "दोपहर:",
    "Morn:": "सुबह:",
    "B:": "सु. -",
    "L:": "दो. -",
    "D:": "रा. -",
    "Take after food": "भोजन के बाद लें",
    "Chewable": "चबाकर खाएं"
  }
};

const translate = (text, lang) => {
  if (!lang || lang === "english" || !translations[lang]) return text;

  // Try exact match first
  if (translations[lang][text]) return translations[lang][text];

  // Case insensitive match
  const lowerText = text.toLowerCase();
  for (const key in translations[lang]) {
    if (key.toLowerCase() === lowerText) return translations[lang][key];
  }

  // Handle combinations or partial matches if needed, but for now exact/case-insensitive is enough
  return text;
};

// Helper to translate dosage components
const translateDose = (dose, lang) => {
  if (!dose || dose === "-") return "-";
  return translate(dose, lang);
};

// Helper function to calculate age
const calculateAge = (dob) => {
  const { years, months, days } = calculateAgeDetailed(dob);
  if (years === 0 && months === 0 && days === 0) return null;
  return `${years} Y ${months} M ${days} D`;
};



// Common Styles
const commonStyles = `
  html, body { height: 100%; margin: 0; padding: 0; }
  body { 
    font-family: Arial, sans-serif; 
    display: flex; 
    flex-direction: column; 
    font-size: 14px;
    background: white;
  }
  .content { flex: 1; padding: 15px; }
  .header { text-align: center; margin-bottom: 5px; }
  .header h1 { margin: 0; font-size: 22px; font-weight: bold; text-transform: uppercase; }
  .header h3 { margin: 4px 0; font-size: 16px; font-weight: 600; }
  .header p { margin: 2px 0; font-size: 12px; }
  
  .patient-info { display: flex; justify-content: space-between; font-size: 13px; }
  .patient-data { margin: 10px 0; font-size: 13px; }
  
  table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 12px; }
  th, td { border: 1px solid #333; padding: 5px; text-align: left; }
  th { background: #f5f5f5; font-weight: bold; }
  
  .advice { margin-top: 10px; font-size: 13px; font-weight: bold; }
  
  .print-footer { padding: 15px; margin-top: auto; border-top: 1px solid #eee; }
  .sig-container { display: flex; justify-content: flex-end; margin-bottom: 30px; }
  .sig-box { text-align: center; border-top: 1px solid #000; width: 220px; padding-top: 5px; font-weight: bold; font-size: 14px; }
  .instruction-list ul { margin: 0; padding-left: 20px; font-size: 11px; color: #333; }
  .instruction-list li { margin-bottom: 2px; }

  @media print {
    body { padding: 0; }
    .content { padding: 5mm; }
    .print-footer { position: relative; bottom: 0; width: 100%; page-break-inside: avoid; }
  }

  /* Specific Adjustments for A4 Paper (Large Width) */
  @media print and (min-width: 17cm) {
    body { font-size: 14pt; }
    .header h1 { font-size: 26pt !important; }
    .header h3 { font-size: 18pt !important; }
    .header p { font-size: 12pt !important; }
    .patient-info, .patient-grid, .meta-info-grid, .patient-data { font-size: 13pt !important; }
    table { font-size: 12pt !important; }
    th { font-size: 11pt !important; }
    .advice { font-size: 13pt !important; margin-top: 20px !important; }
    .sig-box { font-size: 14pt !important; width: 300px !important; margin-top: 40px !important; }
    .instruction-list ul { font-size: 12pt !important; }
    
    /* Modern Template Overrides */
    .hospital-name-modern { font-size: 24pt !important; padding: 12px !important; }
    .hospital-info-modern { font-size: 13pt !important; }
    .header-box h1 { font-size: 28pt !important; }
    .header-box h2 { font-size: 18pt !important; }
    .details-table-modern { font-size: 13pt !important; }
    .vitals-bar { font-size: 12pt !important; padding: 12px 0 !important; }
    table.med-table-modern th { font-size: 11pt !important; padding: 14px 5px !important; }
    table.med-table-modern td { font-size: 13pt !important; padding: 14px 5px !important; }
    .pattern-box { font-size: 14pt !important; }
    .dose-labels { font-size: 12pt !important; }
    .signature-line { width: 300px !important; font-size: 14pt !important; }
  }
`;

// ==========================================
// Template 1: Standard Design
// ==========================================
const getTemplate1HTML = (data) => {
  const { patientData, hospitalData: templateHospitalData, medicines, checkedData, selectedDateTime, authCtx, isCasePaper } = data;
  const hospitalData = data.hospitalData || authCtx?.hospitalData || templateHospitalData;
  const showHeader = hospitalData?.PrescHeader;
  const showFooter = hospitalData?.PrescFooter;
  const hospitalName = hospitalData?.hospitalName || "Hospital Name";
  const hospitalAddress = hospitalData?.hospitalAddress || "";
  const regNumber = hospitalData?.regnumber || "";
  const instructions = hospitalData?.hospitalInstructions || "";
  const lang = data.lang || hospitalData?.prescriptionLanguage || "english";

  const instructionList = instructions.split("\n").filter(line => line.trim() !== "");
  const formattedInstructions = `<ul>${instructionList.map(line => `<li>${line.trim()}</li>`).join("")}</ul>`;

  const patientName = [
    patientData?.patientId?.firstName || patientData?.name || "",
    patientData?.patientId?.lastName || ""
  ].join(" ").trim().toUpperCase();

  const ageDisplay = patientData?.patientId?.dob
    ? calculateAge(patientData.patientId.dob)
    : (patientData?.patientId?.age || patientData?.age ? `${patientData?.patientId?.age || patientData?.age} Y` : "-");

  const dateStr = new Date(checkedData?.billingDate || patientData?.dateTime || selectedDateTime || new Date())
    .toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false })
    .replace(",", "").replace(/\//g, "-");

  return `
    <html>
      <head>
        <title>Prescription</title>
        <style>${commonStyles}</style>
      </head>
      <body>
        <div class="content">
          ${getHeaderHtml(hospitalData)}

          <div class="patient-info" style="margin-top: 10px;">
            <div><strong>Name:</strong> ${patientName} (${ageDisplay}/${patientData?.patientId?.gender || patientData?.gender || "-"})</div>
            <div style="text-align: right;"><strong>Mobile:</strong> ${patientData?.patientId?.mobile || patientData?.mobile || "-"}</div>
          </div>
          <div class="patient-info" style="margin-top: 5px;">
            <div><strong>Patient Id:</strong> ${patientData?.patientId?.patientId || patientData?.ipdAdmissionId || "-"}</div>
            <div style="text-align: right;">${!isCasePaper ? `<strong>${data.isIPD ? "IPD NO:" : "OPD NO:"}</strong> ${patientData?.checkUpId || patientData?.checkupId || patientData?.id || "-"}` : ""}</div>
          </div>
          <div class="patient-info" style="margin-top: 5px;">
            <div>${patientData?.patientId?.registrationNo ? `<strong>Reg. NO:</strong> ${patientData.patientId.registrationNo}` : ""}</div>
            <div style="text-align: right;"><strong>Date:</strong> ${dateStr}</div>
          </div>

          <div class="patient-data">
             ${!isCasePaper && checkedData?.diagnosis ? `<p><strong>Diagnosis:</strong> ${checkedData?.diagnosis}</p>` : ""}
             <p>
               ${[
      checkedData?.weight && checkedData.weight !== "-" ? `<strong>Weight:</strong> ${checkedData.weight}kg` : "",
      checkedData?.bps && checkedData.bps !== "-" ? `<strong>BP:</strong> ${checkedData.bps}/${checkedData.bpd || ""} mm Hg` : "",
      checkedData?.temp && checkedData.temp !== "-" ? `<strong>Temp:</strong> ${checkedData.temp}°F` : "",
      checkedData?.pulse && checkedData.pulse !== "-" ? `<strong>Pulse:</strong> ${checkedData.pulse} bpm` : "",
      checkedData?.spo2 && checkedData.spo2 !== "-" ? `<strong>SpO2:</strong> ${checkedData.spo2}%` : "",
      checkedData?.lmp ? `<strong>LMP:</strong> ${checkedData.lmp}` : "",
      checkedData?.edd ? `<strong>EDD:</strong> ${checkedData.edd}` : "",
      checkedData?.length ? `<strong>Length:</strong> ${checkedData.length}` : "",
      checkedData?.hc ? `<strong>Head Circ.:</strong> ${checkedData.hc}` : ""
    ].filter(x => x).join(", ")}
             </p>
             ${!isCasePaper && checkedData?.complaint ? `<p><strong>Complaint:</strong> ${checkedData?.complaint}</p>` : ""}
          </div>

          ${!isCasePaper ? `
          <table>
            <thead>
              <tr>
                <th>Type</th><th>Medicine</th><th>Dosage</th><th>Breakfast</th><th>Lunch</th><th>Dinner</th><th>Days</th>
              </tr>
            </thead>
            <tbody>
              ${medicines && medicines.length > 0 ? medicines.map(med => `
                <tr>
                  <td style="text-align: center;">${med?.type || med?.medicineType || "-"}</td>
                  <td>
                    <strong>${med?.medicineName}</strong>
                    ${med?.instruction ? `<br/><small style="color: #666;">(${translate(med.instruction, lang)})</small>` : ""}
                  </td>
                  <td style="text-align: center;">${med?.dosage || "-"}</td>
                  <td style="text-align: center;">${translateDose(med?.breakFastDose, lang)}</td>
                  <td style="text-align: center;">${translateDose(med?.lunchDose, lang)}</td>
                  <td style="text-align: center;">${translateDose(med?.dinnerDose, lang)}</td>
                  <td style="text-align: center;">${med?.days || "-"}</td>
                </tr>`).join("") : `<tr><td colspan="7" style="text-align: center;">No medicines prescribed</td></tr>`}
            </tbody>
          </table>

          <div style="margin-top: 15px; font-weight: bold; border-top: 1px solid #eee; padding-top: 10px; display: flex; justify-content: space-between;">
            <span>Advice: ${checkedData?.advice || "-"}</span>
            ${(checkedData?.nextAppointment && checkedData.nextAppointment !== "-" || checkedData?.next_appointment && checkedData.next_appointment !== "-") && !data.isHistory ? `<span>Next Appointment: ${checkedData.nextAppointment || checkedData.next_appointment}</span>` : ""}
          </div>
          ` : ""}
        </div>

        <div class="print-footer">
          <div class="sig-container">
            <div class="sig-box">
              Authority Sign / Doctor Sign
              ${authCtx?.role === "ROLE_DOCTOR" && authCtx?.userName ? `<br/><small>( ${authCtx.userName} )</small>` : ""}
            </div>
          </div>
          ${showFooter ? `<div class="instruction-list">${formattedInstructions}</div>` : ""}
        </div>
      </body>
    </html>
  `;
};

// ==========================================
// Template 2: "Shree Datta Hospital" Style
// ==========================================
const getTemplate2HTML = (data) => {
  const { patientData, hospitalData: templateHospitalData, medicines, checkedData, selectedDateTime, authCtx, isCasePaper } = data;
  const hospitalData = data.hospitalData || authCtx?.hospitalData || templateHospitalData;
  const showHeader = hospitalData?.PrescHeader;
  const showFooter = hospitalData?.PrescFooter;
  const hospitalName = hospitalData?.hospitalName || "Hospital Name";
  const hospitalAddress = hospitalData?.hospitalAddress || "";
  const regNumber = hospitalData?.regnumber || "";
  const instructions = hospitalData?.hospitalInstructions || "";
  const lang = data.lang || hospitalData?.prescriptionLanguage || "english";

  const instructionList = instructions.split("\n").filter(line => line.trim() !== "");
  const formattedInstructions = `<ul>${instructionList.map(line => `<li>${line.trim()}</li>`).join("")}</ul>`;

  const patientName = [
    patientData?.patientId?.firstName || patientData?.name || "",
    patientData?.patientId?.lastName || ""
  ].join(" ").trim().toUpperCase();
  const ageDisplay = patientData?.patientId?.dob
    ? calculateAge(patientData.patientId.dob)
    : (patientData?.patientId?.age || patientData?.age ? `${patientData?.patientId?.age || patientData?.age} Y` : "-");

  const dateStr = new Date(checkedData?.billingDate || patientData?.dateTime || selectedDateTime || new Date())
    .toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false })
    .replace(",", "").replace(/\//g, "-");


  return `
    <html>
      <head>
        <title>Prescription</title>
        <style>
          ${commonStyles}
          body { font-family: 'Times New Roman', serif; }
          .header-box { text-align: center; border-bottom: 2px solid #000; padding-bottom: 5px; margin-bottom: 10px; }
          .header-box h1 { font-size: 24px; font-weight: bold; margin: 0; color: #000; }
          .header-box h2 { font-size: 16px; margin: 4px 0; font-weight: normal; }
          .header-box p { font-size: 11px; margin: 2px 0; }
          
          .patient-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 10px; font-size: 13px; border: 1px solid #ddd; padding: 8px; background: #fdfdfd; }
          .patient-grid div { margin-bottom: 2px; }
          
          table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 11px; border: none; }
          th { border: none; background: #e0e0e0; border-bottom: 2px solid #000; padding: 8px 5px; text-align: left; text-transform: uppercase; font-size: 10px; }
          td { border: none; border-bottom: 1px solid #ddd; padding: 8px 5px; vertical-align: top; }
        </style>
      </head>
      <body>
        <div class="content">
          ${getHeaderHtml(hospitalData)}
          
          <div class="patient-grid">
            <div><strong>Patient:</strong> ${patientName} (${ageDisplay.split(' ')[0]}/${(patientData?.patientId?.gender || patientData?.gender || "-").charAt(0).toUpperCase()})</div>
            <div style="text-align: right;"><strong>Date:</strong> ${dateStr}</div>
            <div><strong>Patient ID:</strong> ${patientData?.patientId?.patientId || patientData?.ipdAdmissionId || "-"}</div>
            <div style="text-align: right;">${!isCasePaper ? `<strong>${data.isIPD ? "IPD NO:" : "OPD NO:"}</strong> ${patientData?.checkUpId || patientData?.checkupId || patientData?.id || "-"}` : ""}</div>
            <div>${patientData?.patientId?.registrationNo ? `<strong>Reg. NO:</strong> ${patientData.patientId.registrationNo}` : ""}</div>
            <div style="text-align: right;"><strong>Mobile:</strong> ${patientData?.patientId?.mobile || patientData?.mobile || "-"}</div>
            <div style="grid-column: span 2; display: flex; justify-content: space-between;">
               <span>${!isCasePaper ? `<strong>Diagnosis:</strong> ${checkedData?.diagnosis || "-"}` : ""}</span>
               <span><strong>Date:</strong> ${dateStr}</span>
            </div>
            <div style="grid-column: span 2;">
              <strong>Vitals:</strong> 
              ${[
      checkedData?.bps && checkedData.bps !== "-" ? `BP: ${checkedData.bps}/${checkedData.bpd || ""}` : "",
      checkedData?.weight && checkedData.weight !== "-" ? `Wt: ${checkedData.weight}` : "",
      checkedData?.pulse && checkedData.pulse !== "-" ? `Pulse: ${checkedData.pulse}` : "",
      checkedData?.temp && checkedData.temp !== "-" ? `Temp: ${checkedData.temp}` : "",
      checkedData?.spo2 && checkedData.spo2 !== "-" ? `SpO2: ${checkedData.spo2}%` : "",
      checkedData?.lmp ? `<strong>LMP:</strong> ${checkedData.lmp}` : "",
      checkedData?.edd ? `<strong>EDD:</strong> ${checkedData.edd}` : "",
      checkedData?.length ? `<strong>Length:</strong> ${checkedData.length}` : "",
      checkedData?.hc ? `<strong>HC:</strong> ${checkedData.hc}` : ""
    ].filter(x => x).join(" | ")}
              ${!isCasePaper && checkedData?.complaint ? `<span style="margin-left: 15px;"><strong>Complaint:</strong> ${checkedData.complaint}</span>` : ""}
            </div>
          </div>

          ${!isCasePaper ? `
          <table>
            <thead>
              <tr>
                <th style="width: 5%;">Sr.</th>
                <th style="width: 12%;">Type</th>
                <th style="width: 25%;">Medicine Name</th>
                <th style="width: 8%;">Dosage</th>
                <th style="width: 20%;">Timing</th>
                <th style="width: 10%;">Days</th>
                <th style="width: 20%;">Instruction</th>
              </tr>
            </thead>
            <tbody>
              ${medicines && medicines.length > 0 ? medicines.map((med, idx) => `
                <tr>
                  <td>${idx + 1}</td>
                  <td>${med?.type || med?.medicineType || "-"}</td>
                  <td><strong>${med?.medicineName}</strong></td>
                  <td>${med?.dosage || "-"}</td>
                  <td>
                    ${translate("B:", lang)} ${translateDose(med?.breakFastDose, lang)} | ${translate("L:", lang)} ${translateDose(med?.lunchDose, lang)} | ${translate("D:", lang)} ${translateDose(med?.dinnerDose, lang)}
                  </td>
                  <td>${med?.days || "-"}</td>
                  <td>${translate(med?.instruction || "", lang) || checkedData?.advice || "-"}</td>
                </tr>`).join("") : `<tr><td colspan="7" style="text-align:center">No Medicines Prescribed</td></tr>`}
            </tbody>
          </table>
          
          <div style="margin-top: 10px; border-top: 1px solid #ddd; padding-top: 10px; font-weight: bold; display: flex; justify-content: space-between;">
            <span>Advice: ${checkedData?.advice || "-"}</span>
            ${(checkedData?.nextAppointment && checkedData.nextAppointment !== "-" || checkedData?.next_appointment && checkedData.next_appointment !== "-") && !data.isHistory ? `<span>Next Appointment: ${checkedData.nextAppointment || checkedData.next_appointment}</span>` : ""}
          </div>
          ` : ""}
        </div>

        <div class="print-footer">
          <div class="sig-container">
            <div class="sig-box">
              Authority Sign / Doctor Sign
              ${authCtx?.role === "ROLE_DOCTOR" && authCtx?.userName ? `<br/><small>( ${authCtx.userName} )</small>` : ""}
            </div>
          </div>
          ${showFooter ? `<div class="instruction-list">${formattedInstructions}</div>` : ""}
        </div>
      </body>
    </html>
  `;
};

// ==========================================
// Template 3: Premium Modern Design
// ==========================================
const getTemplate3HTML = (data) => {
  const { patientData, hospitalData: templateHospitalData, medicines, checkedData, selectedDateTime, authCtx, isCasePaper } = data;
  const hospitalData = data.hospitalData || authCtx?.hospitalData || templateHospitalData;
  const showHeader = hospitalData?.PrescHeader;
  const showFooter = hospitalData?.PrescFooter;
  const hospitalName = hospitalData?.hospitalName || "Hospital Name";
  const hospitalAddress = hospitalData?.hospitalAddress || "";
  const regNumber = hospitalData?.regnumber || "";
  const lang = data.lang || hospitalData?.prescriptionLanguage || "english";

  const patientName = [
    patientData?.patientId?.firstName || patientData?.name || "",
    patientData?.patientId?.lastName || ""
  ].join(" ").trim().toUpperCase();

  const dob = patientData?.patientId?.dob;
  const ageDisplay = dob ? calculateAge(dob) : (patientData?.patientId?.age || patientData?.age || "-");

  const dateStr = new Date(checkedData?.billingDate || patientData?.dateTime || selectedDateTime || new Date())
    .toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true });

  const instructions = hospitalData?.hospitalInstructions || "";
  const instructionList = instructions.split("\n").filter(line => line.trim() !== "");
  const formattedInstructions = `<ul style="margin: 0; padding-left: 20px;">${instructionList.map(line => `<li style="margin-bottom: 5px;">${line.trim()}</li>`).join("")}</ul>`;

  return `
    <html>
      <head>
        <title>Prescription</title>
        <style>
          ${commonStyles}
          body { font-family: 'Times New Roman', serif; }
          .container-page { display: flex; flex-direction: column; height: 100%; }
          .header-modern { text-align: center; margin-bottom: 10px; }
          .hospital-name-modern { background: #e0e0e0; padding: 6px; font-size: 22px; font-weight: bold; text-transform: uppercase; letter-spacing: 1px; border-bottom: 2px solid #333; }
          .hospital-info-modern { font-size: 11px; margin-top: 3px; color: #333; }
          
          .meta-info-grid { display: flex; justify-content: space-between; margin-bottom: 5px; font-size: 13px; }
          .meta-label { font-weight: bold; min-width: 80px; display: inline-block; }
          
          .details-table-modern { width: 100%; border-collapse: collapse; margin-bottom: 8px; font-size: 13px; }
          .details-table-modern td { padding: 3px 0; }
          
          .vitals-bar { border-top: 1px dotted #000; border-bottom: 1px dotted #000; padding: 6px 0; margin-bottom: 10px; display: flex; flex-wrap: wrap; font-size: 12px; gap: 5px 15px; }
          
          table.med-table-modern { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 13px; border: none; }
          table.med-table-modern th { border: none; border-bottom: 1.5px solid #000; border-top: 1.5px solid #000; padding: 10px 5px; text-align: left; text-transform: uppercase; font-size: 12px; background: none; }
          table.med-table-modern td { border: none; padding: 10px 5px; vertical-align: top; }
          
          .pattern-box { font-size: 12px; font-weight: bold; letter-spacing: 2px; color: #000; margin-bottom: 2px; }
          .dose-labels { font-size: 11px; color: #444; }
        </style>
      </head>
      <body>
        <div class="content">
          ${getHeaderHtml(hospitalData)}

          <div class="meta-info-grid">
            <div style="width: 100%;">
              <div style="display: flex; justify-content: space-between; margin-bottom: 5px;">
                <div><strong>Name:</strong> ${patientName} (${ageDisplay}/${patientData?.patientId?.gender || patientData?.gender || "-"})</div>
                <div><strong>Mobile:</strong> ${patientData?.patientId?.mobile || patientData?.mobile || "-"}</div>
              </div>
              <div style="display: flex; justify-content: space-between; margin-bottom: 5px;">
                <div><strong>Patient ID:</strong> ${patientData?.patientId?.patientId || patientData?.ipdAdmissionId || "-"}</div>
                <div>${!isCasePaper ? `<strong>${data.isIPD ? "IPD NO:" : "OPD NO:"}</strong> ${patientData?.checkUpId || patientData?.checkupId || patientData?.id || "-"}` : ""}</div>
              </div>
              <div style="display: flex; justify-content: space-between;">
                <div>${patientData?.patientId?.registrationNo ? `<strong>Reg. NO:</strong> ${patientData.patientId.registrationNo}` : `<strong>Visit Date:</strong> ${dateStr}`}</div>
                <div>${patientData?.patientId?.registrationNo ? `<strong>Visit Date:</strong> ${dateStr}` : (checkedData?.diagnosis || patientData?.diagnosis ? `<strong>Diagnosis:</strong> ${checkedData?.diagnosis || patientData?.diagnosis}` : "")}</div>
              </div>
              ${patientData?.patientId?.registrationNo && (checkedData?.diagnosis || patientData?.diagnosis) ? `
              <div style="display: flex; justify-content: space-between; margin-top: 5px;">
                <div><strong>Diagnosis:</strong> ${checkedData?.diagnosis || patientData?.diagnosis}</div>
              </div>` : ""}
            </div>
          </div>

          <div class="vitals-bar">
            ${[
      checkedData?.weight && checkedData.weight !== "-" ? `<span><strong>Weight:</strong> ${checkedData.weight}</span>` : "",
      checkedData?.pulse && checkedData.pulse !== "-" ? `<span><strong>Pulse:</strong> ${checkedData.pulse}</span>` : "",
      checkedData?.bps && checkedData.bps !== "-" ? `<span><strong>BP:</strong> ${checkedData.bps}/${checkedData.bpd || ""}</span>` : "",
      checkedData?.spo2 && checkedData.spo2 !== "-" ? `<span><strong>SpO2:</strong> ${checkedData.spo2}%</span>` : "",
      checkedData?.lmp ? `<span><strong>LMP:</strong> ${checkedData.lmp}</span>` : "",
      checkedData?.edd ? `<span><strong>EDD:</strong> ${checkedData.edd}</span>` : "",
      checkedData?.length ? `<span><strong>Length:</strong> ${checkedData.length}</span>` : "",
      checkedData?.hc ? `<span><strong>HC:</strong> ${checkedData.hc}</span>` : ""
    ].filter(x => x).join("")}
            ${!isCasePaper && checkedData?.complaint ? `<div style="width: 100%;"><strong>Complaint:</strong> ${checkedData.complaint}</div>` : ""}
          </div>

          ${!isCasePaper ? `
          <table class="med-table-modern">
            <thead>
              <tr>
                <th style="width: 5%;">Sr. No</th>
                <th style="width: 8%;">Type</th>
                <th style="width: 37%;">Drug Name</th>
                <th style="width: 12%;">Dosage</th>
                <th style="width: 28%;">Dose Name</th>
                <th style="width: 10%;">Days</th>
              </tr>
            </thead>
            <tbody>
              ${medicines && medicines.length > 0 ? medicines.map((med, idx) => {
      const pattern = [
        (med?.breakFastDose && med?.breakFastDose !== "-") ? "1" : "-",
        (med?.lunchDose && med?.lunchDose !== "-") ? "1" : "-",
        (med?.dinnerDose && med?.dinnerDose !== "-") ? "1" : "-"
      ].join(" -- ");

      const labels = [
        med?.breakFastDose && med?.breakFastDose !== "-" ? translateDose(med.breakFastDose, lang) : "",
        med?.lunchDose && med?.lunchDose !== "-" ? translateDose(med.lunchDose, lang) : "",
        med?.dinnerDose && med?.dinnerDose !== "-" ? translateDose(med.dinnerDose, lang) : ""
      ].filter(l => l !== "").join(" -- ");

      return `
                  <tr>
                    <td style="text-align: center;">${idx + 1}</td>
                    <td style="font-size: 11px; color: #444;">${med?.medicineType || med?.type || "-"}</td>
                    <td>
                      <div style="font-weight: bold; text-transform: uppercase;">${med?.medicineName}</div>
                      ${med?.instruction ? `<small style="font-size: 12px; color: #666;">(${translate(med.instruction, lang)})</small>` : ""}
                    </td>
                    <td style="text-align: center;">${med?.dosage || "-"}</td>
                    <td>
                      <div class="pattern-box">${pattern}</div>
                      <div class="dose-labels">${labels}</div>
                    </td>
                    <td style="text-align: center;">${med?.days || "-"}</td>
                  </tr>`;
    }).join("") : '<tr><td colspan="7" style="text-align:center;">No Medicines Prescribed</td></tr>'}
            </tbody>
          </table>

          <div style="margin-top: 20px; font-size: 13px; border-top: 2px solid #333; padding-top: 10px; font-weight: bold; display: flex; justify-content: space-between;">
            <span>Advice: ${checkedData?.advice || "-"}</span>
            ${(checkedData?.nextAppointment && checkedData.nextAppointment !== "-" || checkedData?.next_appointment && checkedData.next_appointment !== "-") && !data.isHistory ? `<span>Next Appointment: ${checkedData.nextAppointment || checkedData.next_appointment}</span>` : ""}
          </div>
          ` : ""}
        </div>

        <div class="print-footer">
          <div class="sig-container">
            <div class="sig-box">
              Authority Sign / Doctor Sign
            </div>
          </div>
          ${showFooter ? `<div class="instruction-list" style="font-size: 12px;">${formattedInstructions}</div>` : ""}
        </div>
      </body>
    </html>
  `;
};

// ==========================================
// Template 5: Header with Custom Background Color
// ==========================================
const getTemplate5HTML = (data) => {
  const { patientData, hospitalData: templateHospitalData, medicines, checkedData, selectedDateTime, authCtx, isCasePaper } = data;
  let hospitalData = data.hospitalData || authCtx?.hospitalData || templateHospitalData;
  
  // Ensure Template 5 uses header template 5 and has background color
  hospitalData = {
    ...hospitalData,
    selectHeader: "5", // Force header template 5
  };
  
  const showHeader = hospitalData?.PrescHeader;
  const showFooter = hospitalData?.PrescFooter;
  const lang = data.lang || hospitalData?.prescriptionLanguage || "english";

  const instructionList = (hospitalData?.hospitalInstructions || "").split("\n").filter(line => line.trim() !== "");
  const formattedInstructions = `<ul>${instructionList.map(line => `<li>${line.trim()}</li>`).join("")}</ul>`;

  const patientName = [
    patientData?.patientId?.firstName || patientData?.name || "",
    patientData?.patientId?.lastName || ""
  ].join(" ").trim().toUpperCase();

  const ageDisplay = patientData?.patientId?.dob
    ? calculateAge(patientData.patientId.dob)
    : (patientData?.patientId?.age || patientData?.age ? `${patientData?.patientId?.age || patientData?.age} Y` : "-");

  const dateStr = new Date(checkedData?.billingDate || patientData?.dateTime || selectedDateTime || new Date())
    .toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false })
    .replace(",", "").replace(/\//g, "-");

  const template5Styles = `
    * { box-sizing: border-box; }
    html, body { 
      height: 100%; 
      margin: 0; 
      padding: 0; 
      width: 100%;
      background: white;
    }
    body { 
      margin: 0 !important;
      padding: 0 !important;
      font-family: Arial, sans-serif;
    }
    .template5-wrapper {
      display: flex;
      flex-direction: column;
      height: 100%;
      width: 100%;
    }
    .template5-header { 
      print-color-adjust: exact !important;
      -webkit-print-color-adjust: exact !important;
      -moz-print-color-adjust: exact !important;
      color-adjust: exact !important;
      margin: 0 !important;
      padding: 0 !important;
      width: 100%;
      page-break-inside: avoid;
    }
    .template5-body {
      flex: 1;
      padding: 12px 15px 15px 15px;
      margin: 0;
    }
    .template5-footer {
      margin-top: auto;
      padding: 15px;
    }
    
    @media print {
      html { margin: 0; padding: 0; }
      body { 
        margin: 0 !important;
        padding: 0 !important;
        width: 100%;
      }
      .template5-wrapper { margin: 0; padding: 0; }
      .template5-header {
        margin: 0 !important;
        padding: 0 !important;
        margin-bottom: 12px !important;
        width: 100%;
        page-break-inside: avoid;
      }
      .template5-body {
        padding: 0 15px !important;
        margin: 0 !important;
      }
      .template5-footer {
        margin-top: 15px;
        padding: 15px;
        page-break-inside: avoid;
      }
    }
  `;

  return `
    <html>
      <head>
        <title>Prescription</title>
        <style>
          \${commonStyles}
          \${template5Styles}
          .template5-header { 
            print-color-adjust: exact !important;
            -webkit-print-color-adjust: exact !important;
            -moz-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 12px; }
          th, td { border: 1px solid #333; padding: 5px; text-align: left; }
          th { background: #f5f5f5; font-weight: bold; }
          .patient-info { display: flex; justify-content: space-between; font-size: 13px; margin-bottom: 3px; }
          .patient-data { margin: 8px 0; font-size: 13px; }
        </style>
      </head>
      <body>
        <div class="template5-wrapper">
          <div class="template5-header">
            \${getHeaderHtml(hospitalData)}
          </div>

          <div class="template5-body">
            <div class="patient-info">
              <div><strong>Name:</strong> \${patientName} (\${ageDisplay}/\${patientData?.patientId?.gender || patientData?.gender || "-"})</div>
              <div><strong>Mobile:</strong> \${patientData?.patientId?.mobile || patientData?.mobile || "-"}</div>
            </div>
            <div class="patient-info">
              <div><strong>Patient Id:</strong> \${patientData?.patientId?.patientId || patientData?.ipdAdmissionId || "-"}</div>
              <div>${!isCasePaper ? '<strong>' + (data.isIPD ? 'IPD NO:' : 'OPD NO:') + '</strong> ' + (patientData?.checkUpId || patientData?.checkupId || patientData?.id || '-') : ""}</div>
            </div>
            <div class="patient-info">
              <div>${patientData?.patientId?.registrationNo ? '<strong>Reg. NO:</strong> ' + patientData.patientId.registrationNo : ""}</div>
              <div><strong>Date:</strong> ${dateStr}</div>
            </div>

            <div class="patient-data">
               ${!isCasePaper && checkedData?.diagnosis ? `<p style="margin: 5px 0;"><strong>Diagnosis:</strong> ${checkedData?.diagnosis}</p>` : ""}
               <p style="margin: 5px 0;">
                 ${
                   [
                     checkedData?.weight && checkedData.weight !== "-" ? `<strong>Weight:</strong> ${checkedData.weight}kg` : "",
                     checkedData?.bps && checkedData.bps !== "-" ? `<strong>BP:</strong> ${checkedData.bps}/${checkedData.bpd || ""} mm Hg` : "",
                     checkedData?.temp && checkedData.temp !== "-" ? `<strong>Temp:</strong> ${checkedData.temp}°F` : "",
                     checkedData?.pulse && checkedData.pulse !== "-" ? `<strong>Pulse:</strong> ${checkedData.pulse} bpm` : "",
                     checkedData?.spo2 && checkedData.spo2 !== "-" ? `<strong>SpO2:</strong> ${checkedData.spo2}%` : "",
                     checkedData?.lmp ? `<strong>LMP:</strong> ${checkedData.lmp}` : "",
                     checkedData?.edd ? `<strong>EDD:</strong> ${checkedData.edd}` : "",
                     checkedData?.length ? `<strong>Length:</strong> ${checkedData.length}` : "",
                     checkedData?.hc ? `<strong>Head Circ.:</strong> ${checkedData.hc}` : ""
                   ].filter(x => x).join(", ")
                 }
               </p>
               ${!isCasePaper && checkedData?.complaint ? `<p style="margin: 5px 0;"><strong>Complaint:</strong> ${checkedData?.complaint}</p>` : ""}
            </div>

            ${!isCasePaper ? `
            <table>
              <thead>
                <tr>
                  <th>Type</th><th>Medicine</th><th>Dosage</th><th>Breakfast</th><th>Lunch</th><th>Dinner</th><th>Days</th>
                </tr>
              </thead>
              <tbody>
${
                  medicines && medicines.length > 0 ? medicines.map(med => `
                  <tr>
                    <td style="text-align: center;">${med?.type || med?.medicineType || "-"}</td>
                    <td>
                      <strong>${med?.medicineName}</strong>
                      ${med?.instruction ? `<br/><small style="color: #666;">(${translate(med.instruction, lang)})</small>` : ""}
                    </td>
                    <td style="text-align: center;">${med?.dosage || "-"}</td>
                    <td style="text-align: center;">${translateDose(med?.breakFastDose, lang)}</td>
                    <td style="text-align: center;">${translateDose(med?.lunchDose, lang)}</td>
                    <td style="text-align: center;">${translateDose(med?.dinnerDose, lang)}</td>
                    <td style="text-align: center;">${med?.days || "-"}</td>
                  </tr>`).join("") : `<tr><td colspan="7" style="text-align: center;">No medicines prescribed</td></tr>`
                }
              </tbody>
            </table>

            <div style="margin-top: 12px; font-weight: bold; border-top: 1px solid #ccc; padding-top: 8px; display: flex; justify-content: space-between; font-size: 12px;">
              <span>Advice: ${checkedData?.advice || "-"}</span>
              ${(checkedData?.nextAppointment && checkedData.nextAppointment !== "-" || checkedData?.next_appointment && checkedData.next_appointment !== "-") && !data.isHistory ? `<span>Next Appointment: ${checkedData.nextAppointment || checkedData.next_appointment}</span>` : ""}
            </div>
            ` : ""}
          </div>

          <div class="template5-footer">
            <div style="display: flex; justify-content: flex-end; margin-bottom: 30px;">
              <div style="text-align: center; border-top: 1px solid #000; width: 220px; padding-top: 5px; font-weight: bold; font-size: 14px;">
                Authority Sign / Doctor Sign
                ${authCtx?.role === "ROLE_DOCTOR" && authCtx?.userName ? `<br/><small>( ${authCtx.userName} )</small>` : ""}
              </div>
            </div>
            ${showFooter ? `<div class="instruction-list">${formattedInstructions}</div>` : ""}
          </div>
        </div>
      </body>
    </html>
  `;
};

// Main Export Function
export const getPrescriptionHTML = (templateId, data) => {
  switch (String(templateId)) {
    case "2":
      return getTemplate2HTML(data);
    case "3":
      return getTemplate3HTML(data);
    case "5":
      return getTemplate5HTML(data);
    case "1":
    default:
      return getTemplate1HTML(data);
  }
};
